import { initialFormData } from '../components/AdminAddUserForm/constants.js';
import { parsePrismaDecimal } from '../utils/parsePrismaDecimal.js';
import { parsePersonName } from '../domain/identity/parsePersonName';

const PLACEMENT_STATUS_MAP = {
  Done: 'Placed',
  InProcess: 'In progress',
  'Not Started': 'Not interested',
};

function toYesNoString(value, defaultValue = '') {
  if (value === true || value === 'Yes' || value === 'true') return 'Yes';
  if (value === false || value === 'No' || value === 'false') return 'No';
  return defaultValue;
}

function toYesNoDefaultNo(value) {
  return toYesNoString(value, 'No');
}

function toTriStateYesNo(value) {
  return toYesNoString(value, '');
}

function toDateInputValue(dateValue) {
  if (!dateValue) return '';
  try {
    const dateObj = new Date(dateValue);
    if (!isNaN(dateObj.getTime())) {
      return dateObj.toISOString().split('T')[0];
    }
  } catch {
    // ignore invalid dates
  }
  return '';
}

function inferMarksType(marks, storedType) {
  if (storedType) return storedType;
  if (marks && parseFloat(marks) <= 10) return 'CGPA';
  return 'Percentage';
}

function mapPlacementStatus(oldStatus) {
  return PLACEMENT_STATUS_MAP[oldStatus] || oldStatus || '';
}

function resolveStudent(user, studentOverride) {
  const studentFromUser = user?.student || null;
  if (!studentOverride) return studentFromUser;
  return {
    ...studentFromUser,
    ...studentOverride,
    enrollments: studentOverride.enrollments ?? studentFromUser?.enrollments,
    academicQualifications:
      studentOverride.academicQualifications ?? studentFromUser?.academicQualifications,
    domain: studentOverride.domain ?? studentFromUser?.domain,
  };
}

function parseNameParts(user, student) {
  const parsed = parsePersonName({
    firstName: user?.firstName,
    middleName: user?.middleName,
    lastName: user?.lastName,
    fullName: student?.fullName,
  });
  return {
    firstName: parsed.firstName,
    middleName: parsed.middleName,
    lastName: parsed.lastName,
  };
}

function parseAcademicQualifications(academicQualifications) {
  let tenthMarks = '';
  let tenthYear = '';
  let tenthMarksType = 'Percentage';
  let twelfthMarks = '';
  let twelfthYear = '';
  let twelfthMarksType = 'Percentage';
  let btechMarks = '';
  let btechYear = '';
  let yearOfPassing = '';
  let percentage = '';
  let btechMarksType = 'Percentage';
  let percentageType = 'Percentage';
  let degreeId = '';
  let universityId = '';
  let btechInstitute = '';
  let btechSpecialization = '';
  let educationGap = '';
  let stream = '';
  let department = '';
  let hasGapInEducation = '';
  let gapInEducationJustification = '';
  let hasGapAfterHighestEducation = '';
  let gapAfterHighestEducationJustification = '';
  let mtechMarks = '';
  let mtechYear = '';
  let mtechMarksType = 'Percentage';
  let mtechInstitute = '';
  let mtechSpecialization = '';
  let phdDone = 'No';
  let phdYear = '';

  if (academicQualifications?.length > 0) {
    academicQualifications.forEach((qual) => {
      if (qual.qualificationType === '10th') {
        tenthMarks = parsePrismaDecimal(qual.percentageCgpa);
        tenthYear = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
        tenthMarksType = inferMarksType(tenthMarks, qual.marksType);
      } else if (qual.qualificationType === '12th') {
        twelfthMarks = parsePrismaDecimal(qual.percentageCgpa);
        twelfthYear = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
        twelfthMarksType = inferMarksType(twelfthMarks, qual.marksType);
      } else if (qual.qualificationType === 'M.Tech') {
        mtechMarks = parsePrismaDecimal(qual.percentageCgpa);
        mtechYear = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
        mtechInstitute = qual.universityCollegeName || '';
        mtechSpecialization = qual.specializationBranch || '';
        mtechMarksType = inferMarksType(mtechMarks, qual.marksType);
      } else if (qual.qualificationType === 'PhD') {
        phdDone = 'Yes';
        phdYear = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
      } else if (qual.qualificationType === 'B.Tech') {
        // Always map B.Tech — even when PhD/M.Tech is the highest degree
        degreeId = qual.qualificationType || '';
        universityId = qual.universityCollegeName || '';
        btechInstitute = qual.universityCollegeName || '';
        btechMarks = parsePrismaDecimal(qual.percentageCgpa);
        btechYear = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
        yearOfPassing = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
        percentage = parsePrismaDecimal(qual.percentageCgpa);
        const storedMarksType = inferMarksType(percentage, qual.marksType);
        btechMarksType = storedMarksType;
        percentageType = storedMarksType;
        educationGap = qual.gapJustification === 'Yes' ? 'Yes' : qual.gapJustification ? 'Yes' : 'No';
        stream = qual.specializationBranch || '';
        department = qual.specializationBranch || '';
        btechSpecialization = qual.specializationBranch || '';
        hasGapInEducation = toTriStateYesNo(qual.hasGapInEducation);
        gapInEducationJustification = qual.gapInEducationJustification || '';
        hasGapAfterHighestEducation = toTriStateYesNo(qual.hasGapAfterHighestEducation);
        gapAfterHighestEducationJustification = qual.gapAfterHighestEducationJustification || '';
      } else if (qual.isHighestDegree) {
        // Legacy: other highest-degree types (MCA, etc.) when no dedicated B.Tech row
        if (!btechMarks && !btechInstitute && !yearOfPassing) {
          degreeId = qual.qualificationType || '';
          universityId = qual.universityCollegeName || '';
          btechInstitute = qual.universityCollegeName || '';
          btechMarks = parsePrismaDecimal(qual.percentageCgpa);
          btechYear = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
          yearOfPassing = qual.yearOfPassout ? String(qual.yearOfPassout) : '';
          percentage = parsePrismaDecimal(qual.percentageCgpa);
          const storedMarksType = inferMarksType(percentage, qual.marksType);
          btechMarksType = storedMarksType;
          percentageType = storedMarksType;
          educationGap = qual.gapJustification === 'Yes' ? 'Yes' : qual.gapJustification ? 'Yes' : 'No';
          stream = qual.specializationBranch || '';
          department = qual.specializationBranch || '';
          btechSpecialization = qual.specializationBranch || '';
          hasGapInEducation = toTriStateYesNo(qual.hasGapInEducation);
          gapInEducationJustification = qual.gapInEducationJustification || '';
          hasGapAfterHighestEducation = toTriStateYesNo(qual.hasGapAfterHighestEducation);
          gapAfterHighestEducationJustification = qual.gapAfterHighestEducationJustification || '';
        }
      }
    });
  }

  return {
    tenthMarks,
    tenthYear,
    tenthMarksType,
    twelfthMarks,
    twelfthYear,
    twelfthMarksType,
    btechMarks,
    btechYear,
    yearOfPassing,
    percentage,
    btechMarksType,
    percentageType,
    degreeId,
    universityId,
    btechInstitute,
    btechSpecialization,
    educationGap,
    stream,
    department,
    hasGapInEducation,
    gapInEducationJustification,
    hasGapAfterHighestEducation,
    gapAfterHighestEducationJustification,
    mtechMarks,
    mtechYear,
    mtechMarksType,
    mtechInstitute,
    mtechSpecialization,
    phdDone,
    phdYear,
  };
}

function mapCounsellingSessions(sessions) {
  if (sessions?.length > 0) {
    const mapped = sessions.slice(0, 4).map((session) => {
      let counselorId = '';
      if (session.counselor?.id) {
        counselorId = String(session.counselor.id);
      } else if (session.counselorId) {
        counselorId = String(session.counselorId);
      } else if (session.counselor) {
        counselorId = String(session.counselor.id || session.counselor.counselorId || '');
      }

      return {
        sessionDate: toDateInputValue(session.sessionDate),
        counselorId,
        remarks: session.remarks || '',
      };
    });

    while (mapped.length < 4) {
      mapped.push({ sessionDate: '', counselorId: '', remarks: '' });
    }
    return mapped;
  }

  return initialFormData.counsellingSessions.map((session) => ({ ...session }));
}

function parsePlacementFeedbackRemarks(feedbackRemarks) {
  let location = '';
  let interviewResult = '';
  let offerDetails = '';

  if (!feedbackRemarks) {
    return { location, interviewResult, offerDetails };
  }

  const resultMatch = feedbackRemarks.match(/Interview Result:\s*([^|]+)/i);
  if (resultMatch?.[1]) {
    interviewResult = String(resultMatch[1]).trim();
  }

  const offerMatch = feedbackRemarks.match(/Offer Details:\s*([^|]+)/i);
  if (offerMatch?.[1]) {
    offerDetails = String(offerMatch[1]).trim();
  }

  const locationMatch = feedbackRemarks.match(/Location:\s*([^|]+)/i);
  if (locationMatch?.[1]) {
    location = String(locationMatch[1]).trim();
  }

  if (!interviewResult && !offerDetails && !location) {
    if (feedbackRemarks.toLowerCase().includes('location:')) {
      const oldLocationMatch = feedbackRemarks.match(/Location:\s*([^|]+)/i);
      if (oldLocationMatch?.[1]) {
        location = String(oldLocationMatch[1]).trim();
        let otherNotes = String(feedbackRemarks || '');
        otherNotes = otherNotes
          .replace(/Location:\s*[^|]+\s*\|\s*/i, '')
          .replace(/Location:\s*[^|]+/i, '')
          .trim();
        if (otherNotes && otherNotes !== 'null' && otherNotes !== 'undefined') {
          offerDetails = otherNotes;
        }
      } else {
        offerDetails = feedbackRemarks || '';
      }
    } else {
      offerDetails = feedbackRemarks || '';
    }
  }

  return { location, interviewResult, offerDetails };
}

function mapPlacementData(placement) {
  if (!placement) {
    return { ...initialFormData.placement };
  }

  const placementDateValue = toDateInputValue(placement.placementDate);
  const feedbackRemarks =
    placement.feedbackRemarks || placement.offerDetails || placement.remarks || '';
  const parsedFeedback = parsePlacementFeedbackRemarks(feedbackRemarks);

  let location = parsedFeedback.location;
  let interviewResult = parsedFeedback.interviewResult;
  let offerDetails = parsedFeedback.offerDetails;

  if (!location && (placement.location || placement.workLocation)) {
    location = placement.location || placement.workLocation;
  }
  if (!interviewResult && (placement.result || placement.status)) {
    interviewResult = placement.result || placement.status;
  }
  if (!offerDetails && placement.offerDetails) {
    offerDetails = placement.offerDetails;
  }

  return {
    companyName: placement.companyId
      ? String(placement.companyId)
      : placement.company?.id
        ? String(placement.company.id)
        : '',
    position: placement.jobRole || placement.position || '',
    package: placement.startingPackage
      ? parsePrismaDecimal(placement.startingPackage)
      : placement.currentPackage
        ? parsePrismaDecimal(placement.currentPackage)
        : placement.package
          ? String(placement.package)
          : '',
    location: location || placement.location || placement.workLocation || '',
    interviewDate: toDateInputValue(placement.interviewDate || placement.offerDate),
    placementDate: placementDateValue,
    joiningDate: toDateInputValue(placement.joiningDate),
    result: interviewResult || placement.status || placement.result || '',
    offerDetails,
    shouldMoveToAlumni:
      placement.shouldMoveToAlumni === false ||
      placement.shouldMoveToAlumni === 'false' ||
      placement.shouldMoveToAlumni === 'No'
        ? 'No'
        : 'Yes',
    placementGroupRemovalReason: placement.placementGroupRemovalReason || '',
    removedFromPlacementGroup: placement.removedFromPlacementGroup || false,
  };
}

function mapPrimaryEnrollment(enrollment) {
  if (!enrollment) {
    return {
      batchId: '',
      courseId: '',
      courseCategoryId: '',
      batchStartDate: '',
      courseCompletionDate: '',
      batchStatus: '',
      studentStatus: '',
      attendancePercentage: '',
      remarks: '',
      placementStatus: '',
      appliedForTruechip: '',
      truechipResult: '',
      modeOfCourse: '',
      locationOfCenter: '',
      insuranceTaken: '',
      insuranceDeactivated: '',
      certificationIssued: 'No',
      videoTestimonyReceived: 'No',
      testimonyReceived: 'No',
      counsellingSessions: initialFormData.counsellingSessions.map((session) => ({ ...session })),
      placement: { ...initialFormData.placement },
    };
  }

  return {
    batchId: enrollment.batch?.id ? String(enrollment.batch.id) : '',
    courseId: enrollment.batch?.course?.id ? String(enrollment.batch.course.id) : '',
    courseCategoryId: enrollment.batch?.course?.category?.id
      ? String(enrollment.batch.course.category.id)
      : '',
    batchStartDate: toDateInputValue(enrollment.enrollmentDate),
    courseCompletionDate: toDateInputValue(enrollment.courseCompletionDate),
    batchStatus: enrollment.batchStatus || '',
    studentStatus: enrollment.batchStatus || '',
    attendancePercentage: enrollment.attendancePercentage
      ? String(enrollment.attendancePercentage)
      : '',
    remarks: enrollment.remarks || '',
    placementStatus: mapPlacementStatus(enrollment.placementStatus),
    appliedForTruechip: toTriStateYesNo(enrollment.appliedForTruechip),
    truechipResult: enrollment.truechipResult || '',
    modeOfCourse: enrollment.batch?.mode || '',
    locationOfCenter: enrollment.batch?.location || '',
    insuranceTaken: toTriStateYesNo(enrollment.insuranceTaken),
    insuranceDeactivated: toTriStateYesNo(enrollment.insuranceDeactivated),
    certificationIssued: toYesNoDefaultNo(enrollment.certificationIssued),
    videoTestimonyReceived: toYesNoDefaultNo(enrollment.videoTestimonyReceived),
    testimonyReceived: toYesNoDefaultNo(enrollment.testimonyReceived),
    counsellingSessions: mapCounsellingSessions(enrollment.counsellingSessions),
    placement: mapPlacementData(enrollment.placement),
  };
}

function mapInterviewReadiness(enrollment) {
  const enrollmentId = enrollment?.id != null ? String(enrollment.id) : '';
  const ir = enrollment?.interviewReadiness;

  if (!ir) {
    return {
      ...initialFormData.interviewReadiness,
      enrollmentId,
    };
  }

  return {
    enrollmentId,
    interviewReady: toTriStateYesNo(ir.interviewReady),
    allModulesComplete: toTriStateYesNo(ir.allModulesComplete),
    projectSubmitted: toTriStateYesNo(ir.projectSubmitted),
    studentReadiness: toTriStateYesNo(ir.studentReadiness),
    trainerReadiness: toTriStateYesNo(ir.trainerReadiness),
    resumeReadySubmitted: toTriStateYesNo(ir.resumeReadySubmitted),
    resumeReviewed: toTriStateYesNo(ir.resumeReviewed),
    resumeChangesNeeded: toTriStateYesNo(ir.resumeChangesNeeded),
    resumeSubmissionDeadline: ir.resumeSubmissionDeadline
      ? String(ir.resumeSubmissionDeadline).split('T')[0]
      : '',
    mockInterviewDone: toTriStateYesNo(ir.mockInterviewDone),
    numberOfMockInterviews:
      ir.numberOfMockInterviews != null ? String(ir.numberOfMockInterviews) : '',
    communicationSkillsRating:
      ir.communicationSkillsRating != null ? String(ir.communicationSkillsRating) : '',
    attitudeRating: ir.attitudeRating != null ? String(ir.attitudeRating) : '',
    presentationRating: ir.presentationRating != null ? String(ir.presentationRating) : '',
    technicalReadinessRating:
      ir.technicalReadinessRating != null ? String(ir.technicalReadinessRating) : '',
    areasToImprove: ir.areasToImprove || '',
    feedbackScore: ir.feedbackScore || '',
    inPlacementGroup: toTriStateYesNo(ir.inPlacementGroup),
    inMockGroup: toTriStateYesNo(ir.inMockGroup),
    inUnderpreparedGroup: toTriStateYesNo(ir.inUnderpreparedGroup),
  };
}

function mapTestimony(enrollment) {
  const enrollmentId = enrollment?.id != null ? String(enrollment.id) : '';
  const testimony = enrollment?.testimony;

  if (!testimony) {
    return {
      ...initialFormData.testimony,
      enrollmentId,
    };
  }

  return {
    enrollmentId,
    videoTestimonyReceived: toTriStateYesNo(testimony.videoTestimonyReceived),
    textTestimonyReceived: toTriStateYesNo(testimony.textTestimonyReceived),
    textTestimonyContent: testimony.textTestimonyContent || '',
    wantToContributeAsTrainer: toTriStateYesNo(testimony.wantToContributeAsTrainer),
    feedbackToImproveTraining: testimony.feedbackToImproveTraining || '',
  };
}

function mapAdditionalEnrollments(enrollments) {
  if (!enrollments || enrollments.length <= 1) return [];

  return enrollments.slice(1).map((enrollment) => ({
    id: enrollment.id != null ? String(enrollment.id) : '',
    batchName: enrollment.batch?.batchName || enrollment.batch?.name || '',
    batchStatus: enrollment.batchStatus || '',
    placementStatus: mapPlacementStatus(enrollment.placementStatus),
    attendancePercentage: enrollment.attendancePercentage
      ? parsePrismaDecimal(enrollment.attendancePercentage)
      : '',
    remarks: enrollment.remarks || '',
    courseCompletionDate: toDateInputValue(enrollment.courseCompletionDate),
  }));
}

/**
 * Map user (usersAPI.getByEmail) + optional student (studentsAPI.getById) to admin edit form shape.
 * Suitable for dispatch({ type: 'LOAD_DATA', data: ... }).
 */
export function mapUserStudentToEditForm(user, studentOverride = null) {
  const student = resolveStudent(user, studentOverride);
  const { firstName, middleName, lastName } = parseNameParts(user, student);
  const academic = parseAcademicQualifications(student?.academicQualifications);
  const primaryEnrollment =
    student?.enrollments?.length > 0 ? student.enrollments[0] : null;
  const enrollmentFields = mapPrimaryEnrollment(primaryEnrollment);
  const domainId = student?.domain?.id ? String(student.domain.id) : '';

  return {
    firstName,
    middleName,
    lastName,
    email: user?.email || '',
    mobile: student?.phoneNumber || '',
    whatsapp: student?.whatsapp || student?.phoneNumber || '',
    linkedIn: student?.linkedIn != null ? student.linkedIn : '',
    department: academic.department,
    gender: student?.gender || '',
    currentCity:
      student?.currentLocation ||
      student?.currentCity ||
      (student?.currentAddress
        ? student.currentAddress.includes(',')
          ? student.currentAddress.split(',').pop().trim()
          : student.currentAddress.trim()
        : ''),
    diplomaDone: student?.diplomaDone || '',
    stream: academic.stream,
    semester: '',
    tenthMarks: academic.tenthMarks,
    tenthMarksType: academic.tenthMarksType,
    twelfthMarks: academic.twelfthMarks,
    twelfthMarksType: academic.twelfthMarksType,
    btechMarks: academic.btechMarks,
    btechMarksType: academic.btechMarksType,
    tenthYear: academic.tenthYear,
    twelfthYear: academic.twelfthYear,
    btechYear: academic.btechYear,
    btechSpecialization: academic.btechSpecialization,
    mtechMarks: academic.mtechMarks,
    mtechYear: academic.mtechYear,
    mtechMarksType: academic.mtechMarksType,
    mtechInstitute: academic.mtechInstitute,
    mtechSpecialization: academic.mtechSpecialization,
    phdDone: academic.phdDone,
    phdYear: academic.phdYear,
    educationGap: academic.educationGap,
    hasGapInEducation: academic.hasGapInEducation,
    gapInEducationJustification: academic.gapInEducationJustification,
    hasGapAfterHighestEducation: academic.hasGapAfterHighestEducation,
    gapAfterHighestEducationJustification: academic.gapAfterHighestEducationJustification,
    profilePreference: student?.profilePreference || '',
    modeOfCourse: enrollmentFields.modeOfCourse,
    locationOfCenter: enrollmentFields.locationOfCenter,
    yearOfPassing: academic.yearOfPassing,
    percentage: academic.percentage,
    percentageType: academic.percentageType,
    btechInstitute: academic.btechInstitute,
    motivation: student?.motivation || '',
    resumeUrl: student?.resumeUrl || '',
    bio: student?.bio || '',
    notes: student?.notes || '',
    pincode: student?.pincode || '',
    address: student?.currentAddress || '',
    permanentAddress: student?.permanentAddress || '',
    joiningKitAddress: student?.joiningKitAddress || '',
    joiningKitReceived: student?.joiningKitReceived || '',
    parentsName: student?.parentsName || '',
    parentsPhone: student?.parentsPhone || '',
    parentsEmail: student?.parentsEmail || '',
    guardianName: student?.guardianName || '',
    guardianContact: student?.guardianContact || '',
    guardianEmail: student?.guardianEmail || '',
    employeeId: student?.employeeId || '',
    experienceYears: student?.experienceYears ? String(student.experienceYears) : '',
    availability: student?.availability || '',
    dob: toDateInputValue(student?.dob),
    degree: String(academic.degreeId),
    university: String(academic.universityId),
    country: student?.countryId || '',
    state: student?.stateId || '',
    cityId: student?.cityId || '',
    heardFrom: student?.heardFromId || '',
    role: user?.role || '',
    courseId: String(enrollmentFields.courseId),
    batchId: String(enrollmentFields.batchId),
    courseCategoryId: String(enrollmentFields.courseCategoryId),
    domainId: String(domainId),
    batchStartDate: enrollmentFields.batchStartDate,
    courseCompletionDate: enrollmentFields.courseCompletionDate,
    attendancePercentage: enrollmentFields.attendancePercentage,
    studentStatus: enrollmentFields.studentStatus,
    batchStatus: enrollmentFields.batchStatus,
    placementStatus: enrollmentFields.placementStatus,
    appliedForTruechip: enrollmentFields.appliedForTruechip,
    truechipResult: enrollmentFields.truechipResult,
    remarks: enrollmentFields.remarks,
    insuranceTaken: enrollmentFields.insuranceTaken,
    insuranceDeactivated: enrollmentFields.insuranceDeactivated,
    certificationIssued: enrollmentFields.certificationIssued,
    videoTestimonyReceived: enrollmentFields.videoTestimonyReceived,
    testimonyReceived: enrollmentFields.testimonyReceived,
    veStudentId: student?.id || '',
    password: '',
    counsellingSessions: enrollmentFields.counsellingSessions,
    placement: enrollmentFields.placement,
    interviewReadiness: mapInterviewReadiness(primaryEnrollment),
    testimony: mapTestimony(primaryEnrollment),
    additionalEnrollments: mapAdditionalEnrollments(student?.enrollments),
  };
}
